// Copy of boards accounts/close--period and accounts/close--locked (spec
// ledger-bank-close.md §6/§7, HANDOFF §7). Strings the boards draw are
// verbatim; the few the boards do not draw are marked PROPOSED and logged in
// OPEN-QUESTIONS.md. Month names come from the API (`month_label_ar`) or the
// locale (Iraqi months); digits are Arabic-Indic via lib/format.

import { arNumber, formatDate, formatPeriod, formatTime } from '@/lib/format';

/** 'YYYY-MM' → the next month 'YYYY-MM' (calendar arithmetic, not money). */
export function nextMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

/** 'YYYY-MM' → its last day 'YYYY-MM-DD'. */
export function monthEnd(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${month}-${String(d).padStart(2, '0')}`;
}

/** Month name without the year: '2026-08' → «آب». */
export const monthOnly = (month: string) => formatPeriod(month, { year: false });

/** «بند واحد ناقص» (board) · «بندان ناقصان» · «٣ بنود ناقصة» · «١١ بنداً ناقصاً» (PROPOSED, CLS-2). */
export function missingLabel(n: number): string {
  if (n === 1) return 'بند واحد ناقص';
  if (n === 2) return 'بندان ناقصان';
  if (n >= 3 && n <= 10) return `${arNumber(n)} بنود ناقصة`;
  return `${arNumber(n)} بنداً ناقصاً`;
}

/** «١٥ أيلول ٩:٠٢ ص» — the closed pill's timestamp (Baghdad). */
export function closedAtLabel(iso: string): string {
  return `${formatDate(iso)} ${formatTime(iso)}`;
}

export const CLOSE_COPY = {
  eyebrow: 'الحسابات · إقفال الفترة',
  menu: 'إقفال الفترة',
  /** «إقفال آب ٢٠٢٦» */
  title: (monthLabel: string) => `إقفال ${monthLabel}`,
  /** «آب ٢٠٢٦ · مقفل» */
  lockedTitle: (monthLabel: string) => `${monthLabel} · مقفل`,
  /** «مفتوحة · آخر فترة مقفلة تموز» */
  pillOpen: (lastClosedMonth: string | null) => (lastClosedMonth ? `مفتوحة · آخر فترة مقفلة ${monthOnly(lastClosedMonth)}` : 'مفتوحة'),
  /**
   * Board: «أقفلته د. سارة · ١٥ أيلول ٩:٠٢ ص». The API carries no gender for
   * the closer, so the verb is the §6 pill word «مقفل» (OPEN-QUESTIONS LCK-5).
   */
  pillClosed: (name: string | null, closedAt: string | null) =>
    ['مقفل', name, closedAt ? closedAtLabel(closedAt) : null].filter(Boolean).join(' · '),
  /** «تموز · مقفل» */
  chip: (month: string, state: 'open' | 'closed') => (state === 'closed' ? `${monthOnly(month)} · مقفل` : monthOnly(month)),
  chipsLabel: 'الفترة',
  statement: 'كشف الإقفال',
  reopen: 'إعادة فتح',

  checklistTitle: 'قبل الإقفال',
  whatTitle: 'ماذا يفعل الإقفال',
  /** The four lines; designer refs «(ل، ض)» «(غ)» dropped (spec §6). Codes stay LTR via isolate. */
  what: (month: string, carryCode: string) => {
    const end = formatDate(monthEnd(month));
    const name = monthOnly(month);
    return [
      `يمنع إنشاء أو تعديل أي مستند بتاريخ حتى ${end}؛ التصحيح بقيد عكس في ${monthOnly(nextMonth(month))}.`,
      `يرحّل ربح ${name} إلى الأرباح المرحّلة (${carryCode}) ويصفّر حسابات الإيراد والمصروف للفترة.`,
      `يُثبّت التقارير ل${name} ويُسجَّل الإقفال باسمك في سجل التدقيق.`,
      'إعادة الفتح للمالك فقط، بسبب، وتُسجَّل.',
    ];
  },
  summaryTitle: (month: string) => `ملخّص ${monthOnly(month)}`,
  summary: {
    revenue: 'الإيراد',
    expenses: 'المصروفات وتكلفة المواد',
    net: 'صافي الربح',
    carry: 'يُرحَّل إلى',
  },
  /** «إقفال آب» (HANDOFF §7 «إقفال {الشهر}») */
  closeButton: (month: string) => `إقفال ${monthOnly(month)}`,
  /** «إقفال آب · بند واحد ناقص» */
  closeButtonMissing: (month: string, missing: number) => `إقفال ${monthOnly(month)} · ${missingLabel(missing)}`,
  /** PROPOSED: a month that has not ended cannot close. */
  closeButtonNotEnded: (month: string) => `إقفال ${monthOnly(month)} · لم ينتهِ الشهر بعد`,
  /** PROPOSED: offline — the checks need the server. */
  closeButtonOffline: (month: string) => `إقفال ${monthOnly(month)} · بلا اتصال`,

  // closed view (board 7)
  kpi: {
    documents: (month: string) => `مستندات ${monthOnly(month)}`,
    documentsUnit: 'مرحَّلة',
    net: 'صافي الربح المرحَّل',
    unitIqd: 'د.ع',
    reversals: 'قيود عكس بعد الإقفال',
    reversalsUnit: (month: string) => `في ${monthOnly(nextMonth(month))}`,
    reopens: 'إعادة فتح',
    reopensUnit: 'مرات',
  },
  lockedHelper: 'الفترة المقفلة تُقرأ وتُطبع ولا تُعدَّل. الحوار يظهر عند أي محاولة تعديل على مستند داخلها، من أي شاشة.',

  // confirm close (PROPOSED, CLS-4)
  confirmTitle: (month: string) => `إقفال ${monthOnly(month)}؟`,
  confirmBody: (month: string) =>
    `يُقفل التاريخ حتى ${formatDate(monthEnd(month))} ويُرحَّل صافي الربح، ولا يُعدَّل أي مستند داخل ${monthOnly(month)} بعدها إلا بقيد عكس.`,
  cancel: 'إلغاء',
  closedToast: (month: string) => `أُقفل ${monthOnly(month)}.`,

  // owner reopen (PROPOSED reason sheet, LCK-3)
  reopenTitle: (month: string) => `إعادة فتح ${monthOnly(month)}`,
  reopenBody: 'إعادة الفتح للمالك فقط، بسبب، وتُسجَّل.',
  reasonLabel: 'السبب',
  reasonRequired: 'السبب مطلوب',
  reopenConfirm: 'إعادة فتح',
  reopenedToast: (month: string) => `أُعيد فتح ${monthOnly(month)}.`,
  /** The owner's / manager's inbox on the close page. */
  requestsTitle: 'طلبات إعادة الفتح',
  requestBy: (name: string | null, at: string) => `${name ?? '—'} · ${formatDate(at)}`,

  // first use (PROPOSED): no month has ended yet
  emptyTitle: (month: string) => `أول إقفال يكون بعد انتهاء ${monthOnly(month)}.`,
  emptyBody: 'تُفحص قائمة الإقفال آلياً حين ينتهي الشهر.',
  emptyAction: 'القيود',
} as const;

/** The global refusal dialog (board accounts/close--locked, HANDOFF §5.8 / §7). */
export const LOCKED_COPY = {
  title: 'فترة مقفلة',
  /** Second sentence of the board's body (after the server's «… لا يمكن تعديلها.»). */
  fix: 'لتصحيح المبلغ أنشئ قيد عكس بتاريخ اليوم، أو اطلب من المالك إعادة فتح الفترة بسبب.',
  /** The board sentence cut at «أو»: the reversal exit only (no reopen exit for this person). */
  fixReverseOnly: 'لتصحيح المبلغ أنشئ قيد عكس بتاريخ اليوم.',
  /** PROPOSED: no reversal for this document (e.g. a bank statement). */
  fixRequestOnly: 'لتصحيحه اطلب من المالك إعادة فتح الفترة بسبب.',
  reverse: 'قيد عكس بتاريخ اليوم',
  request: 'طلب إعادة الفتح',
  /** The reason step of a staff / manager request (sheet not drawn — PROPOSED like the owner's). */
  requestBody: (month: string) => `يصل الطلب إلى المالك ليعيد فتح ${monthOnly(month)}؛ اكتب السبب.`,
  requestSend: 'إرسال الطلب',
  back: 'رجوع',
  requestedToast: 'أُرسل الطلب إلى المالك',
  alreadyRequestedToast: 'الطلب مُرسل إلى المالك من قبل',
  cancel: 'إلغاء',
  reversedToast: (entryNo?: string | null) => (entryNo ? `أُنشئ قيد العكس ${entryNo} بتاريخ اليوم.` : 'أُنشئ قيد العكس بتاريخ اليوم.'),
  offline: 'بلا اتصال · العكس والطلب يحتاجان الاتصال.',
  failed: 'لم تنجح المحاولة.',
} as const;
